import {
  ItemTypeConsts, LdapCustomAttributesConsts, StoreModelMap, StoreModuleMap,
} from 'client/util/Constants';
import { cloneDeep } from 'lodash';
import LdapServerApi from 'client/api/v2/LdapServerApi';
import TranslationRuleApi from 'client/api/v2/TranslationRuleApi';
import UserGroupApi from 'client/api/v2/UserGroupApi';
import Vue from 'vue';
import createModel from 'client/store/modules/models/createModel';

export const getDefaultItem = () => ({
  id: '',
  name: 'JumpCloud LDAP',
  userLockoutAction: '',
  userPasswordExpirationAction: '',
});

const model = createModel(getDefaultItem, 'id', LdapServerApi);
const sambaModel = StoreModelMap[ItemTypeConsts.samba];
const systemUserCollection = StoreModuleMap[ItemTypeConsts.systemUser];
const userGroupCollection = StoreModuleMap[ItemTypeConsts.userGroup];

export default {
  namespaced: true,

  state: {
    ...model.state,
  },

  actions: {
    ...model.actions,

    async fetchItem(context) {
      await model.actions.fetchItem(context);

      const { id } = context.state.item;
      await context.dispatch(`${sambaModel}/fetchItem`, { ldapServerId: id }, { root: true });
    },

    async fetchLdapCustomAttributes(context) {
      const { commit } = context;
      const { id } = context.state.item;
      const attributes = await TranslationRuleApi
        .fetch(LdapCustomAttributesConsts.LdapDirectoryType, id);
      const mappedAttributes = {};

      [...Array(3)].forEach((_, index) => {
        const consecutiveNumber = index + 1;
        const actualAttribute = attributes.find(attribute => attribute.mapping.external.value.slice(-1) === `${consecutiveNumber}`);
        let attributeId;
        let value;

        if (actualAttribute) {
          attributeId = actualAttribute.id;
          value = actualAttribute.mapping.internal.value;
        } else {
          attributeId = null;
          value = LdapCustomAttributesConsts.None;
        }

        mappedAttributes[index] = {
          id: attributeId,
          isCustomAttribute: true,
          mutable: true,
          name: `Attribute ${consecutiveNumber}`,
          readOnly: false,
          tempId: index,
          value,
        };
      });

      commit('SET_LDAP_CUSTOM_ATTRIBUTES', mappedAttributes);
      commit('SET_PREVIOUS_LDAP_CUSTOM_ATTRIBUTES', mappedAttributes);
    },

    resetItem(context) {
      model.actions.resetItem(context);
      context.dispatch(`${sambaModel}/resetItem`, null, { root: true });
    },

    saveItem({ dispatch, state }) {
      const { id } = state.item;
      return dispatch(`${sambaModel}/saveItem`, { ldapServerId: id }, { root: true });
    },

    async saveLdapBindDns({ dispatch, getters }) {
      const ldapBindingUsersToUpdate = getters.getLdapBindingUsersToUpdate;

      const ids = Object.keys(ldapBindingUsersToUpdate);
      const attributes = Object.values(ldapBindingUsersToUpdate);
      const payload = { ids, attributes };

      return dispatch(`${systemUserCollection}/updateItems`, payload, { root: true });
    },

    updateLdapCustomAttributes(context, { index, newListItem }) {
      const { commit } = context;
      const { ldapCustomAttributes } = context.state;

      const updatedLdapAttributes = cloneDeep(ldapCustomAttributes);
      updatedLdapAttributes[index] = newListItem;

      commit('SET_LDAP_CUSTOM_ATTRIBUTES', updatedLdapAttributes);
    },

    async updateUserGroupsSambaAuthentication({ rootGetters }) {
      const enabledGroupsToUpdate = rootGetters[`${userGroupCollection}/getSambaEnabledGroupsToUpdate`];
      const disabledGroupsToUpdate = rootGetters[`${userGroupCollection}/getSambaDisabledGroupsToUpdate`];

      if (enabledGroupsToUpdate.length === 0 && disabledGroupsToUpdate.length === 0) {
        return;
      }

      const tasks = [];

      [...enabledGroupsToUpdate, ...disabledGroupsToUpdate].forEach((groupToUpdate) => {
        tasks.push(() => UserGroupApi.update(groupToUpdate));
      });

      // eslint-disable-next-line no-restricted-syntax
      for (const task of tasks) {
        // eslint-disable-next-line no-await-in-loop
        await task();
      }
    },

    async saveLdapCustomAttributes(context, rule) {
      const { id } = context.state.item;
      await TranslationRuleApi.create(
        LdapCustomAttributesConsts.LdapDirectoryType,
        id,
        rule,
        LdapCustomAttributesConsts.Mapping,
      );
    },

    async deleteLdapCustomAttributes(context, { attributeId }) {
      const { id } = context.state.item;
      await TranslationRuleApi
        .delete(LdapCustomAttributesConsts.LdapDirectoryType, id, attributeId);
    },
  },

  getters: {
    ...model.getters,

    displayName() {
      return 'JumpCloud LDAP';
    },

    distinguishedName(state, getters, rootState) {
      const { orgId } = rootState.SettingsModule;
      return `o=${orgId},dc=jumpcloud,dc=com`;
    },

    getLdapCustomAttributes(state) {
      return state.ldapCustomAttributes;
    },

    getLdapBindingUsersToUpdate(state, getters, rootState, rootGetters) {
      return rootGetters[`${systemUserCollection}/getLdapBindingUsersToUpdate`];
    },

    getPreviousLdapCustomAttributes(state) {
      return state.previousLdapCustomAttributes;
    },

    hasItemChanged(state, getters, rootState, rootGetters) {
      return rootGetters[`${sambaModel}/hasItemChanged`];
    },

    haveLdapBindDnsChanged(state, getters) {
      const ldapBindingUsersToUpdate = getters.getLdapBindingUsersToUpdate;
      return Object.keys(ldapBindingUsersToUpdate).length > 0;
    },
  },

  mutations: {
    ...model.mutations,

    SET_PREVIOUS_LDAP_CUSTOM_ATTRIBUTES(state, attributes) {
      Vue.set(state, 'previousLdapCustomAttributes', attributes);
    },

    SET_LDAP_CUSTOM_ATTRIBUTES(state, attributes) {
      Vue.set(state, 'ldapCustomAttributes', attributes);
    },
  },
};
